
import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Clock, User, Eye } from "lucide-react";
import { format, isSameDay, parseISO } from "date-fns";
import { ProviderBooking } from "@/hooks/useProviderBookings";
import { useIsMobile } from "@/hooks/use-mobile";

interface BookingsCalendarProps {
  bookings: ProviderBooking[];
  onViewDetails: (bookingId: string) => void;
}

const BookingsCalendar = ({ bookings, onViewDetails }: BookingsCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const isMobile = useIsMobile();

  // Get bookings for the selected date
  const selectedDateBookings = useMemo(() => {
    if (!selectedDate) return [];
    
    return bookings.filter(booking => {
      const bookingDate = parseISO(booking.booking_date);
      return isSameDay(bookingDate, selectedDate);
    }).sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [bookings, selectedDate]);

  // Get dates that have bookings for calendar highlighting
  const bookingDates = useMemo(() => {
    return bookings.map(booking => parseISO(booking.booking_date));
  }, [bookings]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        {/* Mobile Calendar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-lg">
              <CalendarIcon className="h-5 w-5" />
              <span>Calendar</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="w-full"
              modifiers={{
                hasBookings: bookingDates,
              }}
              modifiersStyles={{
                hasBookings: {
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  fontWeight: 'bold',
                },
              }}
            />
            <div className="mt-3 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                <span>Days with bookings</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile Selected Date Bookings */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {selectedDate ? format(selectedDate, 'MMM dd, yyyy') : 'Select a date'}
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {selectedDateBookings.length} booking{selectedDateBookings.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="px-3">
            {selectedDateBookings.length > 0 ? (
              <div className="space-y-3">
                {selectedDateBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-3 border rounded-lg bg-white"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-sm">
                          {booking.start_time} - {booking.end_time}
                        </span>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getStatusColor(booking.status)}`}
                      >
                        {booking.status}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                      <User className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{booking.customer_name}</span>
                    </div>
                    
                    <div className="text-xs text-gray-500 mb-2">
                      {booking.service_name || 'Unknown Service'}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        #{booking.booking_number}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetails(booking.id)}
                        className="h-8 px-2"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CalendarIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No bookings for this date</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calendar */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5" />
            <span>Bookings Calendar</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            className="rounded-md border"
            modifiers={{
              hasBookings: bookingDates,
            }}
            modifiersStyles={{
              hasBookings: {
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                fontWeight: 'bold',
              },
            }}
          />
          <div className="mt-4 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
              <span>Days with bookings</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Bookings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              {selectedDate ? format(selectedDate, 'MMMM dd, yyyy') : 'Select a date'}
            </span>
            <Badge variant="outline">
              {selectedDateBookings.length} booking{selectedDateBookings.length !== 1 ? 's' : ''}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDateBookings.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {selectedDateBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-sm">
                          {booking.start_time} - {booking.end_time}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${getStatusColor(booking.status)}`}
                        >
                          {booking.status}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                        <User className="h-3 w-3" />
                        <span className="truncate">{booking.customer_name}</span>
                      </div>
                      
                      <div className="text-xs text-gray-500 truncate">
                        {booking.service_name || 'Unknown Service'}
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-1">
                        #{booking.booking_number}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(booking.id)}
                      className="ml-2 h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No bookings for this date</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingsCalendar;
